// Select basato su Radix Select — accessibile con label e gestione errore
import * as RadixSelect from '@radix-ui/react-select'
import { ChevronDown, ChevronUp, Check } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

interface SelectProps {
  options: SelectOption[]
  value?: string
  onValueChange?: (value: string) => void
  placeholder?: string
  label?: string
  error?: string
  disabled?: boolean
  className?: string
  id?: string
  required?: boolean
}

export function Select({
  options,
  value,
  onValueChange,
  placeholder = 'Seleziona...',
  label,
  error,
  disabled,
  className,
  id,
  required,
}: SelectProps) {
  const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-')

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label htmlFor={inputId} className="text-sm font-medium text-[var(--fc-foreground)]">
          {label}
          {required && <span className="ml-1 text-[var(--fc-danger)]" aria-hidden="true">*</span>}
        </label>
      )}
      <RadixSelect.Root value={value} onValueChange={onValueChange} disabled={disabled}>
        <RadixSelect.Trigger
          id={inputId}
          className={cn(
            'flex h-10 w-full items-center justify-between rounded border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm',
            'focus:border-[var(--fc-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--fc-primary)]',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'data-[placeholder]:text-[var(--fc-foreground-subtle)]',
            error && 'border-[var(--fc-danger)]',
            className
          )}
          aria-invalid={Boolean(error)}
        >
          <RadixSelect.Value placeholder={placeholder} />
          <RadixSelect.Icon>
            <ChevronDown className="h-4 w-4 text-[var(--fc-foreground-muted)]" />
          </RadixSelect.Icon>
        </RadixSelect.Trigger>

        <RadixSelect.Portal>
          <RadixSelect.Content
            className="z-50 min-w-[8rem] overflow-hidden rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] shadow-[var(--fc-shadow-md)]"
            position="popper"
            sideOffset={4}
          >
            <RadixSelect.ScrollUpButton className="flex h-6 cursor-default items-center justify-center bg-[var(--fc-surface)] text-[var(--fc-foreground-muted)]">
              <ChevronUp className="h-4 w-4" />
            </RadixSelect.ScrollUpButton>
            <RadixSelect.Viewport className="max-h-[var(--radix-select-content-available-height)] p-1">
              {options.map((opt) => (
                <RadixSelect.Item
                  key={opt.value}
                  value={opt.value}
                  disabled={opt.disabled}
                  className={cn(
                    'relative flex cursor-pointer select-none items-center rounded px-8 py-2 text-sm outline-none',
                    'text-[var(--fc-foreground)] hover:bg-[var(--fc-surface-overlay)] focus:bg-[var(--fc-surface-overlay)]',
                    'data-[disabled]:pointer-events-none data-[disabled]:opacity-50'
                  )}
                >
                  <RadixSelect.ItemIndicator className="absolute left-2">
                    <Check className="h-4 w-4 text-[var(--fc-primary)]" />
                  </RadixSelect.ItemIndicator>
                  <RadixSelect.ItemText>{opt.label}</RadixSelect.ItemText>
                </RadixSelect.Item>
              ))}
            </RadixSelect.Viewport>
            <RadixSelect.ScrollDownButton className="flex h-6 cursor-default items-center justify-center bg-[var(--fc-surface)] text-[var(--fc-foreground-muted)]">
              <ChevronDown className="h-4 w-4" />
            </RadixSelect.ScrollDownButton>
          </RadixSelect.Content>
        </RadixSelect.Portal>
      </RadixSelect.Root>
      {error && (
        <p className="text-xs text-[var(--fc-danger)]" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
